import { FuseNavigationItem } from '@fuse/components/navigation';
import { Message } from 'app/layout/common/messages/messages.types';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import { Profile } from 'app/modules/profile/profile.model';
import { OptionsSelect as KeyValue } from './shared/ashcorp-controllers/select/select.component';

export interface InitialData {
    messages: Message[];
    navigation: {
        compact: FuseNavigationItem[],
        default: FuseNavigationItem[],
        futuristic: FuseNavigationItem[],
        horizontal: FuseNavigationItem[]
    };
    notifications: Notification[];
    shortcuts: Shortcut[];
    user: Profile;
}

export interface ToasterMessage {
    message: string;
    type: 'success' | 'error';
}

export interface ApiResponse<T = any> {
    id: string;
    // TODO: httpCode is not part of the documented API envelope but may be injected by server middleware.
    httpCode: number;
    code: string;
    title: string;
    status: 'success' | 'error' | 'alert';
    payload: T;
    // TODO: errors field is not in the documented API envelope — verify backend usage.
    errors: T;
    message: string;
}

export interface ValidationError {
    error: string;
    name: string;
    label: string;
    type: string;
    received: string;
    expected: string;
    message: string;
}


export interface E_Payload {
    error_id: string;
    id: ValidationError;
    code: ValidationError;
    name: ValidationError;
    status: ValidationError;
}

export interface ValidationErrorPayload {
    [key: string]: ValidationError | string;
}

/**
 * Pagination request parameters — aligned with Admin API spec.
 * Fields: page, size, search, status, order_by, order_dir.
 */
export class QPaged<T> {
    page?: number = 1;
    size?: number = 10;
    search?: string = '';
    /** Column to sort by (e.g. 'id', 'email', 'created_at'). */
    order_by?: string = 'id';
    /** Sort direction: 'asc' or 'desc'. */
    order_dir?: 'asc' | 'desc' = 'desc';
    exclusive?: boolean = false;

    conditions?: T;
}

/** @deprecated Use `QPaged` with `order_by`/`order_dir`. Kept for backward compatibility. */
export class PQueryPaged<T> extends QPaged<T> { }

export interface PStatusUpdate {
    id: number;
    status: string;
}

export interface PLifecycleUpdate {
    id: number;
    status?: string;
    banner_enabled?: boolean;
    banner_message?: string | null;
    banner_severity?: string | null;
}

export interface WorkspaceLifecycle {
    status: string;
    readonly: boolean;
    blocked: boolean;
    unavailable?: boolean;
    banner: {
        enabled: boolean;
        message: string | null;
        severity: string | null;
    };
}

/**
 * Paginated list response — aligned with Admin API spec.
 * The API returns: items, page, size, total, pages.
 * `count` is kept for backward compatibility with existing components.
 */
export interface RQueryPaged<T> {
    items: T[];
    /** @deprecated Use `total` instead. Kept for backward compatibility. */
    count: number;
    total: number;
    page: number;
    size: number;
    pages: number;
}

export interface ROptionItem {
    id: number;
    label: string;
}

export interface RLayoutItem {
    theme: string;
    logo: string;
    watermark: string;
    favicon: string;
}

export interface RContactItem {
    email: string;
    lastname: string;
    othernames: string;
    mobile: string;
}

export interface RUserIdentityItem {
    id?: number;
    email?: string;
    fullname?: string;
}

export interface RMetaItem {
    created_by?: RUserIdentityItem;
    created_on?: string;
    updated_by?: RUserIdentityItem;
    updated_on?: string;
}

export const OPTIONS_STATUS: KeyValue = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' }
] as const;

export const OPTIONS_LIFECYCLE_STATUS: KeyValue = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Freeze (read-only)', value: 'FREEZE' },
    { label: 'Block', value: 'INACTIVE' }
] as const;

export const OPTIONS_BANNER_SEVERITY: KeyValue = [
    { label: 'Info', value: 'info' },
    { label: 'Promo', value: 'promo' },
    { label: 'Warning', value: 'warning' },
    { label: 'Error', value: 'error' }
] as const;

export const OPTIONS_ACCESS: KeyValue = [
    { label: 'Granted', value: 'GRANTED' },
    { label: 'Revoked', value: 'REVOKED' }
] as const;

export const OPTIONS_ROLE: KeyValue = [
    { label: 'Super Admin', value: '@SUPERADMIN' },
    { label: "Admin", value: "@ADMIN" },
    { label: "Manager", value: "@MANAGER" },
    { label: "User", value: "@USER" }
] as const;

export const MODULE_COMPONENTS: KeyValue = [
    { label: 'Dashboard', value: 'dashboard' },
    { label: 'Accounts', value: 'accounts' },
    { label: 'Campaigns', value: 'campaigns' },
    { label: 'Payroll', value: 'payroll' },
    { label: 'Settings', value: 'settings' },
    { label: 'Users', value: 'users' }
] as const;

export type EOptionsStatus = typeof OPTIONS_STATUS[number];
export type EOptionsAccess = typeof OPTIONS_ACCESS[number];
export type EOptionsRole = typeof OPTIONS_ROLE[number];
export type EOptionModule = typeof MODULE_COMPONENTS[number];